// Bereich „Bearbeitung“ der Schadenmappe: Kopfkarte, Beteiligte, Notiz und
// Verlauf — Karten in voller Breite. Die Zusatzangaben ohne eigene Spalte
// (`meldung.mappe`, siehe mappeDaten.ts) werden hier lokal geführt und bei
// jeder Änderung vollständig gespeichert.
import * as React from "react";
import type { BigAnswers } from "../../schadenmeldung-big/types";
import type { AbschlussWerte } from "../meldung/AbschlussIntern";
import type { DamagePatch, SchadenmappeData } from "../types";
import { Kopfkarte } from "./Kopfkarte";
import { BeteiligteKarte } from "./BeteiligteKarte";
import { mappeAusMeldung, type MappeZusatz } from "./mappeDaten";
import "./bearbeiten.css";

// Der Arbeitsstrang des Vorgangs bringt Editor und Dokument-Betrachter mit —
// erst laden, wenn der Bereich wirklich geöffnet wird.
const VerlaufKarte = React.lazy(() => import("./VerlaufKarte"));

export interface BearbeitungBereichProps {
  data: SchadenmappeData;
  /** Antworten der Meldung (lokaler Stand der Seite). */
  answers: BigAnswers;
  onAntworten: (patch: Partial<BigAnswers>) => void;
  abschluss: AbschlussWerte;
  onAbschluss: (patch: Partial<AbschlussWerte>) => void;
  /** Teil-PUT des Schadens (Sofort-Speichern der Seite). */
  onPatch: (patch: DamagePatch) => void | Promise<void>;
}

export function BearbeitungBereich({ data, answers, onAntworten, abschluss, onAbschluss, onPatch }: BearbeitungBereichProps) {
  const { damage } = data;

  // Lokal geführt und nur beim Wechsel des Schadens neu aus dem Server gesät —
  // ein stilles Nachladen überschreibt keine laufende Eingabe.
  const [zusatz, setZusatz] = React.useState<{ damageId: number; mappe: MappeZusatz }>(() => ({
    damageId: damage.id,
    mappe: mappeAusMeldung(damage.meldung),
  }));
  if (zusatz.damageId !== damage.id) {
    setZusatz({ damageId: damage.id, mappe: mappeAusMeldung(damage.meldung) });
  }
  const mappe = zusatz.damageId === damage.id ? zusatz.mappe : mappeAusMeldung(damage.meldung);

  const onMappe = (patch: Partial<MappeZusatz>) => {
    const neu = { ...mappe, ...patch };
    setZusatz({ damageId: damage.id, mappe: neu });
    void onPatch({ meldung: { mappe: neu } });
  };

  return (
    <div className="smb-bereich">
      <Kopfkarte
        damage={damage}
        answers={answers}
        onAntworten={onAntworten}
        abschluss={abschluss}
        onAbschluss={onAbschluss}
        onPatch={onPatch}
        mappe={mappe}
        onMappe={onMappe}
      />
      <BeteiligteKarte damage={damage} answers={answers} mappe={mappe} onMappe={onMappe} />
      <React.Suspense
        fallback={
          <section className="smb-karte" aria-label="Historie" aria-busy="true">
            <p className="smb-leer">Lädt …</p>
          </section>
        }
      >
        <VerlaufKarte damage={damage} requiredDocuments={data.requiredDocuments} />
      </React.Suspense>
    </div>
  );
}
